import React, { useState } from 'react';
import {
  Card,
  Accordion,
  useAccordionToggle,
  Row,
  Col,
  Nav,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import Tippy from '@tippyjs/react';
import Avatar from '../Avatar';
import { NO_DATA, HELPER_TEXT_BILLABLE_BREAKS } from '../../constants';
import { WeeklyStatusApprovalToggle, ExportOptions } from './index';
import { sToHMSColonSeparated, groupBy } from '../../helpers';
import MonthlyReportModal from './MonthlyReportModal';
import { NoDataAlert } from '../common';

const ApproveTimeCardContent = ({
  isLoading,
  timecardDetails,
  statusUpdate,
  handleMonthlyReport,
  monthlyReportData,
  selectedMonthForReport,
}) => {
  const [panelIndex, setPanelIndex] = useState();

  const CustomToggle = ({ children, eventKey }) => {
    const customOnClick = useAccordionToggle(eventKey, () => {
      setPanelIndex(eventKey === panelIndex ? null : eventKey);
    });

    const customClass = eventKey === panelIndex ? 'opened' : 'collapsed';

    return (
      <Nav.Link
        className={`${customClass} hd-accordion-button`}
        onClick={customOnClick}
      >
        {children}
      </Nav.Link>
    );
  };

  CustomToggle.propTypes = {
    children: PropTypes.any,
    eventKey: PropTypes.any,
  };

  const [showMonthlyReportModal, setShowMonthlyReportModal] = useState(false);

  return (
    <>
      <Card>
        <section>
          <ExportOptions
            timesheetDetails={timecardDetails}
            viewTab="timeCard"
            setShowMonthlyReportModal={setShowMonthlyReportModal}
          />
          {timecardDetails.length > 0 ? (
            <Accordion>
              {timecardDetails.map((week) => (
                <section
                  key={`approval-tc-${week._id}`}
                  className="position-relative"
                >
                  <Card.Header>
                    <Row className="align-items-center">
                      <Col>
                        <CustomToggle
                          eventKey={week._id}
                          className="text-start"
                        >
                          <div className="d-flex">
                            {week.user && (
                              <div className="avatar avatar-xs me-2">
                                <Avatar
                                  firstName={week.user.firstName}
                                  lastName={week.user.lastName}
                                  defaultBg="#55cbcd"
                                />
                              </div>
                            )}{' '}
                            <span>
                              {week.user.lastName
                                ? `${week.user.firstName} ${week.user.lastName}`
                                : week.user.firstName}
                            </span>
                          </div>
                        </CustomToggle>
                      </Col>
                      <Col className="col-auto p-0">
                        <WeeklyStatusApprovalToggle
                          status={week.status}
                          logs={week.approvalLogs}
                          wsWeekDetails={week.wsWeekDetails}
                          wsWeekStatusUpdate={statusUpdate}
                          weeklySubmissionId={
                            week.approvalId !== null ? week.approvalId : ''
                          }
                          userId={week.user._id}
                        />
                      </Col>
                    </Row>
                  </Card.Header>
                  <Accordion.Collapse eventKey={week._id}>
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover card-table">
                          <thead className="thead-light">
                            <tr>
                              <th>&nbsp;</th>
                              <th className="text-center">Clock in</th>
                              <th className="text-center">Clock out</th>
                              <th className="text-center">Total</th>
                              <th className="text-center">
                                Breaks{' '}
                                <Tippy content={HELPER_TEXT_BILLABLE_BREAKS}>
                                  <i className="mdi mdi-information-outline text-dark" />
                                </Tippy>
                              </th>
                              <th className="text-center">Productive</th>
                              <th>&nbsp;</th>
                            </tr>
                          </thead>
                          <tbody>
                            {week.data.map((day) => {
                              let breakTypesList = [];
                              // eslint-disable-next-line no-unused-vars
                              const sessionBreakTypes =
                                day.workSessions.length > 0 &&
                                day.totalBreakTimeSeconds > 0
                                  ? day.workSessions.map((item) =>
                                      item.breakTimeSeconds > 0
                                        ? (breakTypesList = [
                                            ...breakTypesList,
                                            ...item.break,
                                          ])
                                        : null
                                    )
                                  : [];

                              // Group break list by name
                              const breakTypeArray = groupBy(
                                breakTypesList,
                                'type'
                              );

                              return (
                                <tr key={`day${day.date}`}>
                                  <td>
                                    {moment(day.date).format('ddd, MMM DD')}
                                    {day.schedule && day.schedule.isHoliday && (
                                      <span className="badge bg-danger-soft ms-2 rounded-1">
                                        Holiday
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {day.clockIn
                                      ? moment(day.clockIn).format('hh:mm:ss A')
                                      : '-'}
                                  </td>
                                  <td className="text-center">
                                    {day.clockOut
                                      ? moment(day.clockOut).format(
                                          'hh:mm:ss A'
                                        )
                                      : '-'}
                                  </td>
                                  <td className="text-center">
                                    {day.totalWorkSessionSeconds > 0
                                      ? sToHMSColonSeparated(
                                          day.totalWorkSessionSeconds,
                                          'hh:mm:ss'
                                        )
                                      : '00:00:00'}
                                  </td>
                                  <td className="text-center">
                                    {day.totalBreakTimeSeconds > 0 ? (
                                      <Tippy
                                        content={
                                          <div
                                            style={{ maxWidth: '200px' }}
                                            className="px-2 pt-1 pb-2"
                                          >
                                            {Object.entries(breakTypeArray)
                                              .sort()
                                              .map((breakArr, breakIndex) => {
                                                const groupedBreakType = [
                                                  ...breakArr[1],
                                                ];
                                                const breakTypeTotal =
                                                  groupedBreakType.reduce(
                                                    (pValue, cValue) =>
                                                      pValue + cValue.breakTime,
                                                    0
                                                  );
                                                return (
                                                  <Row
                                                    key={breakIndex}
                                                    className="pb-2"
                                                  >
                                                    <Col>
                                                      {groupedBreakType[0]
                                                        .breakName
                                                        ? `${
                                                            groupedBreakType[0]
                                                              .breakName
                                                          } ${
                                                            groupedBreakType[0]
                                                              .shortCode
                                                              ? `(${groupedBreakType[0].shortCode})`
                                                              : ''
                                                          } ${
                                                            groupedBreakType[0]
                                                              .billable
                                                              ? '*'
                                                              : ''
                                                          }`
                                                        : 'Other'}
                                                    </Col>
                                                    <Col className="col-auto">
                                                      <small>
                                                        {sToHMSColonSeparated(
                                                          breakTypeTotal / 1000,
                                                          'hh:mm:ss'
                                                        )}
                                                      </small>
                                                    </Col>
                                                  </Row>
                                                );
                                              })}
                                          </div>
                                        }
                                        theme="light"
                                      >
                                        <span>
                                          {sToHMSColonSeparated(
                                            day.totalBreakTimeSeconds,
                                            'hh:mm:ss'
                                          )}
                                        </span>
                                      </Tippy>
                                    ) : (
                                      '00:00:00'
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {day.totalWorkSessionSeconds > 0
                                      ? sToHMSColonSeparated(
                                          day.totalWorkSessionSeconds -
                                            day.totalNonBillableBreakTimeSeconds,
                                          'hh:mm:ss'
                                        )
                                      : '00:00:00'}
                                  </td>
                                  <th>&nbsp;</th>
                                </tr>
                              );
                            })}

                            <tr>
                              <td colSpan={3} align="right">
                                &nbsp;
                              </td>
                              <td className="text-center">
                                <strong>
                                  {sToHMSColonSeparated(
                                    week.totalWorkSessionSeconds,
                                    'hh:mm:ss'
                                  )}
                                </strong>
                              </td>
                              <td className="text-center">
                                <strong>
                                  {sToHMSColonSeparated(
                                    week.totalBreakTimeSeconds,
                                    'hh:mm:ss'
                                  )}
                                </strong>
                              </td>
                              <td className="text-center">
                                <strong>
                                  {sToHMSColonSeparated(
                                    week.totalWorkSessionSeconds -
                                      week.totalBreakTimeSeconds,
                                    'hh:mm:ss'
                                  )}
                                </strong>
                              </td>
                              <td>&nbsp;</td>
                            </tr>
                          </tbody>
                        </table>
                        <hr className="m-0" />
                      </div>
                    </Card.Body>
                  </Accordion.Collapse>
                </section>
              ))}
            </Accordion>
          ) : (
            !isLoading && <NoDataAlert content={NO_DATA.WORKSESSION} />
          )}
        </section>
      </Card>
      {showMonthlyReportModal && (
        <MonthlyReportModal
          showMonthlyReportModal={showMonthlyReportModal}
          setShowMonthlyReportModal={setShowMonthlyReportModal}
          handleMonthlyReport={handleMonthlyReport}
          monthlyReportData={monthlyReportData}
          selectedMonthForReport={selectedMonthForReport}
        />
      )}
    </>
  );
};

ApproveTimeCardContent.propTypes = {
  isLoading: PropTypes.bool,
  timecardDetails: PropTypes.array,
  statusUpdate: PropTypes.func,
  handleMonthlyReport: PropTypes.func,
  monthlyReportData: PropTypes.array,
  selectedMonthForReport: PropTypes.string,
};

export default ApproveTimeCardContent;
